/**
 * Prefetch storage: save/load prefetched products, categories, subcategories per store.
 * Uses IndexedDB (large quota, works in Safari/iOS) with fallback and migration from localStorage.
 */

import { getApiBaseUrl } from "@/lib/apiBase";

const PREFIX = "reshet_prefetch_";
const DB_NAME = "reshet_prefetch_db";
const DB_VERSION = 1;
const STORE_NAME = "prefetch";

export function getPrefetchKey(storeId) {
  return `${PREFIX}${storeId}`;
}

function openDB() {
  if (typeof indexedDB === "undefined") return Promise.reject(new Error("IndexedDB not available"));
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: "storeId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function normalizePayload(storeId, payload) {
  return {
    storeId,
    timestamp: Date.now(),
    products: payload.products || [],
    categories: payload.categories || [],
    subcategories: payload.subcategories || [],
    subsubcategories: payload.subsubcategories || [],
    store: payload.store || null,
    companies: payload.companies || [],
    productsByCompany: payload.productsByCompany || {},
    chain: payload.chain && typeof payload.chain === "object" ? payload.chain : null,
  };
}

function normalizeLoaded(data) {
  if (!data?.products || !Array.isArray(data.products)) return null;
  return {
    storeId: data.storeId,
    timestamp: data.timestamp || 0,
    products: data.products,
    categories: Array.isArray(data.categories) ? data.categories : [],
    subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
    subsubcategories: Array.isArray(data.subsubcategories) ? data.subsubcategories : [],
    store: data.store || null,
    companies: Array.isArray(data.companies) ? data.companies : [],
    productsByCompany: data.productsByCompany && typeof data.productsByCompany === "object" ? data.productsByCompany : {},
    chain: data.chain && typeof data.chain === "object" ? data.chain : null,
  };
}

/**
 * Load prefetched data (async). Prefers IndexedDB; migrates from localStorage once if present.
 * @param {string} storeId
 * @returns {Promise<{ storeId: string, timestamp: number, products: any[], categories: any[], subcategories: any[], store: any, companies: any[], productsByCompany: Record<string, any[]> } | null>}
 */
export function loadPrefetched(storeId) {
  if (!storeId) return Promise.resolve(null);

  return openDB()
    .then((db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(storeId);
        req.onsuccess = () => {
          const data = req.result ? normalizeLoaded(req.result) : null;
          if (data) {
            resolve(data);
            return;
          }
          // Migrate from legacy localStorage if present
          try {
            const raw = localStorage.getItem(getPrefetchKey(storeId));
            if (!raw) {
              resolve(null);
              return;
            }
            const parsed = JSON.parse(raw);
            const migrated = normalizeLoaded(parsed);
            if (migrated) {
              // Return migrated data; next "התחל הזמנה" will save to IndexedDB (avoids TDZ/circular call issues)
              resolve(migrated);
            } else {
              resolve(null);
            }
          } catch {
            resolve(null);
          }
        };
        req.onerror = () => reject(req.error);
      });
    })
    .catch(() => {
      // IndexedDB unavailable (e.g. private mode in some browsers): try localStorage
      try {
        const raw = localStorage.getItem(getPrefetchKey(storeId));
        if (!raw) return null;
        return Promise.resolve(normalizeLoaded(JSON.parse(raw)));
      } catch {
        return Promise.resolve(null);
      }
    });
}

/**
 * Save prefetched data (async). Uses IndexedDB; throws on failure so UI can show error.
 * @param {string} storeId
 * @param {{ products: any[], categories: any[], subcategories: any[], store: any, companies?: any[], productsByCompany?: Record<string, any[]> }} payload
 * @returns {Promise<void>}
 */
export function savePrefetched(storeId, payload) {
  if (!storeId) return Promise.resolve();

  const data = normalizePayload(storeId, payload);
  // Build fallback payload synchronously so async .catch() doesn't touch `data` (avoids TDZ in some runtimes)
  const reducedForFallback = {
    ...data,
    companies: (data.companies || []).map((c) => {
      const { logo_data_url, ...rest } = c;
      return rest;
    }),
    chain: data.chain && typeof data.chain === "object" ? (() => {
      const { logo_data_url, ...rest } = data.chain;
      return rest;
    })() : null,
  };

  return openDB()
    .then((db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(data);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
        tx.onerror = () => reject(tx.error);
      });
    })
    .catch(() => {
      // Fallback: try localStorage with reduced payload (no base64 logos) to stay under quota
      try {
        localStorage.setItem(getPrefetchKey(storeId), JSON.stringify(reducedForFallback));
        return;
      } catch (e) {
        console.warn("prefetchStorage.savePrefetched failed", e);
        throw new Error("לא ניתן לשמור את הקטלוג במכשיר (מגבלת אחסון). נסה בדפדפן אחר או נקה נתוני אתר.");
      }
    });
}

/**
 * Clear prefetched data for a store (async).
 * @param {string} storeId
 */
export function clearPrefetched(storeId) {
  if (!storeId) return Promise.resolve();

  return openDB()
    .then((db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(storeId);
        req.onsuccess = () => {
          try {
            localStorage.removeItem(getPrefetchKey(storeId));
          } catch (_) {}
          resolve();
        };
        req.onerror = () => reject(req.error);
      });
    })
    .catch(() => {
      try {
        localStorage.removeItem(getPrefetchKey(storeId));
      } catch (_) {}
      return Promise.resolve();
    });
}

/** Cache API name for prefetched images */
export const PREFETCH_IMAGE_CACHE = "reshet-prefetch-images";

/** בסיס API לאותו לוגיקה כמו axios (כולל ‎VITE_API_URL=/api‎ + proxy) */
function getEffectiveApiBase() {
  return getApiBaseUrl();
}

/**
 * Same URL we use for prefetch cache – use this for every img/src so offline serves from cache.
 * When forPrefetch=true we add skipOptimize=1 so backend streams S3 without sharp (~30s for 1000 images).
 * @param {string} rawUrl - S3 or external image URL
 * @param {boolean} [forPrefetch] - true = URL for prefetch (fast path); same URL must be used when displaying prefetched data
 * @returns {string|null} proxy URL (same-origin) or null
 */
export function getProxyImageUrl(rawUrl, forPrefetch = false) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const u = rawUrl.trim();
  if (!u) return null;
  const apiBase = getEffectiveApiBase();
  if (u.startsWith(apiBase)) return u;
  const base = `${apiBase}/products/optimized-image?imageUrl=${encodeURIComponent(u)}`;
  return forPrefetch ? `${base}&skipOptimize=1` : base;
}

/**
 * Check if Cache API is available (e.g. secure context).
 * @returns {boolean}
 */
export function isImageCacheAvailable() {
  return typeof caches !== "undefined";
}
